import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { useBillingEvents } from "@/common/hooks/queries/useBillingEvents";
import { useProducts } from "@/common/hooks/queries/useProducts";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { BillingEventData, BillingEventTableItem } from "@/common/types/billing/event.type";
import { ClientCompanyBranchData } from "@/common/types/client/companyBranch.type";
import { ProductData } from "@/common/types/product.type";
import { fromInternalBaseOptions } from "@/features/billingBills/constants/fromInternalBaseOptions";
import { EstablishmentCompanyTransformer } from "@/features/companyBranches/services/establishmentCompanyTransformer";
import { useClientUsers } from "@/features/users/hooks/useClientUsers";
import { ClientUserData } from "@/features/users/types/clientUsers";
import { Receipt, SearchIcon } from "lucide-react";
import billingEventsTableColumns from "../../constants/billingEventsTableColumns.constant";
import { BillingEventTransformer } from "../../services/billingEventTransformer";
import { useEstablishmentCompaniesWithConsumption } from "@/common/hooks/queries/useEstablishmentCompaniesWithConsumption";
import { formatCurrency } from "@/common/utils/formatters";
import { SheetFilters } from "@/common/components/SheetFilters";
import ListFilters from "@/common/components/ListFilters";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import useCompanyRemainingCreditAndBalance from "@/common/hooks/queries/useCompanyRemainingCreditAndBalance";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { useAvailableCompanyBranches } from "@/features/companyBranches/hooks/useAvailableCompanyBranches";

const BillingEventsList = () => {
  const { filterParams, setFilterParams } = useFilterParams({
    fromInternalBase: fromInternalBaseOptions.find(({ value }) => value === 'false')
  });

  const { date, formattedDate } = useFilterDate(filterParams);

  const [searchEstablishmentCompany, setSearchEstablishmentCompany] = useDebounce('');
  const [searchCompanyBranch, setSearchCompanyBranch, instantSearchCompanyBranch] = useDebounce('');

  const { hasUserPermission } = useUserPermissions();

  const hasRemainingCreditAndBalancePermission = hasUserPermission('Client::Companies::RemainingCreditAndBalance');

  const { data: company } = useMineCompany();

  const { data: productsOptions } = useProducts({
    mapper: Mapper.mapToOptions<ProductData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const { data: authorOptions } = useClientUsers({
    mapper: Mapper.mapToOptions<ClientUserData>({ valueFieldName: 'id', labelFieldName: 'full_name' }),
  });

  const { data: branchOptions } = useAvailableCompanyBranches({
    filters: {
      search: searchCompanyBranch,
    },
    mapper: Mapper.mapToOptions<ClientCompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const { data: establishmentCompanyOptions } = useEstablishmentCompaniesWithConsumption({
    filters: { search: searchEstablishmentCompany },
    mapper: Mapper.mapWithTransformer({ transformer: EstablishmentCompanyTransformer.optionTransformer() }),
  });

  const { data: companyRemainingCreditAndBalance } = useCompanyRemainingCreditAndBalance();

  const {
    data: billingEvents,
    meta,
    isFetching: billingEventsIsFetching,
    error: billingEventsError = {} as any,
    refetch: refetchBillingEvents,
  } = useBillingEvents({
    filters: {
      page: filterParams?.page || 1,
      search: filterParams?.search || '',
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to,
      productId: filterParams?.product?.value || '',
      authorId: filterParams?.author?.value || '',
      branchId: filterParams?.branch?.value || '',
      establishmentBranchId: filterParams.establishmentCompany?.value,
      fromInternalBase: filterParams.fromInternalBase?.value,
    },
    mapper: Mapper.mapWithTransformer<BillingEventData[], BillingEventTableItem[]>({
      transformer: BillingEventTransformer.tableTransformer({}),
    })
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <ListHeader
          Icon={<Receipt className="w-10 h-10" strokeWidth={1.5} />}
          title="Transações"
        />
      </div>

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={billingEventsError?.status}
        refetchFunction={refetchBillingEvents}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e: any) => setFilterParams((params) => ({ ...params, search: e.target.value }))}
              value={filterParams?.search}
            />

            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <DatePickerWithRange
                  date={date}
                  setDate={(date) => setFilterParams((params) => ({ ...params, date }))}
                />

                <ListFilters
                  filters={[
                    {
                      className: "z-50 max-w-60",
                      onChange: (e: any) => setFilterParams((params) => ({ ...params, establishmentCompany: e.value ? e : null })),
                      value: filterParams?.establishmentCompany,
                      onInputChange: setSearchEstablishmentCompany,
                      options: [{ label: "Todas", value: '' }, ...(establishmentCompanyOptions || [])],
                      placeholder: "Estabelecimento",
                    },
                    {
                      className: 'w-50 max-w-60',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, fromInternalBase: e, })),
                      value: filterParams.fromInternalBase,
                      options: [{ label: 'Todas', value: '' }, ...fromInternalBaseOptions],
                      placeholder: 'Exibir transações da base',
                    },
                  ]}
                />
                <SheetFilters
                  filters={[
                    {
                      label: 'Produto',
                      options: [
                        {
                          label: 'Todas',
                          value: '',
                          selected: !filterParams.product?.value,
                          onClick: () => setFilterParams(({ product, ...prev }) => ({ ...prev }))
                        },

                        ...(productsOptions?.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.product?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, product: { label, value } }))
                        })) || [])
                      ]
                    },

                    {
                      label: 'Autor',
                      options: [
                        {
                          label: 'Todas',
                          value: '',
                          selected: !filterParams.author?.value,
                          onClick: () => setFilterParams(({ author, ...prev }) => ({ ...prev }))
                        },

                        ...(authorOptions?.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.author?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, author: { label, value } }))
                        })) || [])
                      ]
                    },

                    {
                      label: 'Filial',
                      options: [
                        {
                          label: 'Todas',
                          value: '',
                          selected: !filterParams.branch?.value,
                          onClick: () => setFilterParams(({ branch, ...prev }) => ({ ...prev }))
                        },

                        ...(branchOptions?.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.branch?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, branch: { label, value } }))
                        })) || [])
                      ],
                      component: (
                        <TextField
                          variant="filter"
                          LeftIcon={<SearchIcon size={14} />}
                          placeholder="Pesquisar"
                          className="mb-2"
                          value={instantSearchCompanyBranch}
                          onChange={(e) => setSearchCompanyBranch(e.currentTarget.value)}
                        />
                      ),
                    },

                  ]}
                />
              </div>
              {company?.kind === 'private_company' && hasRemainingCreditAndBalancePermission && (
                <div className='flex items-center gap-4'>
                  <div>
                    <p className='text-sm italic font-thin text-white-400'>Limite disponível</p>
                    <span className='text-lg'>{formatCurrency(Number(companyRemainingCreditAndBalance?.remaining_credit))}</span>
                  </div>
                  <div>
                    <p className='text-sm italic font-thin text-white-400'>Saldo disponível</p>
                    <span className='text-valid text-lg'>{formatCurrency(Number(companyRemainingCreditAndBalance?.remaining_balance))}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Table
            headerClassName="z-30"
            columns={company?.kind === 'private_company' ? billingEventsTableColumns : billingEventsTableColumns.filter((column) => column.value !== 'from_client_balance')}
            isLoadingData={billingEventsIsFetching}
            rows={billingEvents}
            rowEmptyValue="--"
            hasPagination
            page={filterParams.page || 1}
            setPage={(p) => setFilterParams((params) => ({ ...params, page: p }))}
            totalPages={meta.total_pages}
            totalCount={meta.total}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default BillingEventsList;
